/* A BUILD IS ONE THING. THE CART DID NOT KNOW THAT. — Sapphire Row
   ---------------------------------------------------------------------------
   `RB.addToCart` posts a build as TWO line items joined by `_build_id`, and
   nothing outside ring-builder.js had ever read that property. So the two lines
   were independent: remove the gemstone and the MOUNTING stayed, priced and
   checkout-able on its own. Found by the owner 2026-08-02 by doing exactly that.

   That is not a cosmetic problem. A bare mounting is not a product this business
   sells — `meta-tags.liquid` noindexes `ring-builder-setting` precisely because it
   is "a bare mounting a shopper could mistake for a ring", and the settings were
   deliberately pulled out of new-arrivals and search for the same reason. Selling
   one is shipping a customer a half-finished ring with no stone in it.

   THE RULE IS ASYMMETRIC, and deliberately so (owner, 2026-08-02):

       remove the STONE   -> the setting CANNOT stand alone   -> both go
       remove the SETTING -> a loose stone is a real product   -> the stone stays

   So this never blocks anyone from buying a gemstone on its own; it only refuses
   to sell the metal half by itself.

   ⚠️ WHY A REPAIR PASS AND NOT ONLY A UI CHANGE. Grouping the two rows behind one
   Remove control (below) stops it happening from here on. It does nothing about a
   cart that ALREADY holds an orphan — carts persist for days, the owner's test
   cart is in that state right now, and `/cart/change.js` can be reached without
   this page. So the pass runs on every cart render and heals what it finds.

   ⚠️ THIS IS THE CART, NOT THE CHECKOUT. Shopify's checkout is not themeable
   here, so it still lists the two lines separately. Merging them into ONE line
   the way Stuller does — parent line, components nested underneath — is a Cart
   Transform Function (Shopify Functions), which has to be deployed as an app
   extension and is a separate piece of work. This file makes the CART behave
   correctly and read as one item; it cannot change what checkout draws. */
(function () {
  'use strict';

  var BUILD = '_build_id', PART = '_rb_part';

  // Styles ride with the module rather than in their own asset: this is one
  // self-contained behaviour, and a second file is a second thing to remember to
  // push. Uses Edge's own tokens so it inherits the theme rather than fighting it.
  var CSS = '' +
    '.rb-build{border:1px solid rgba(35,74,103,.18);border-radius:.6rem;margin:0 0 1.6rem;overflow:hidden}' +
    '.rb-build__head{display:flex;align-items:center;justify-content:space-between;' +
      'gap:1.2rem;padding:1.1rem 1.4rem;border-bottom:1px solid rgba(35,74,103,.14)}' +
    '.rb-build__title{font-weight:600;color:#234A67}' +
    '.rb-build__remove{appearance:none;background:none;border:0;padding:0;font:inherit;' +
      'color:#6b7a86;text-transform:uppercase;letter-spacing:.06em;font-size:.82em;cursor:pointer}' +
    '.rb-build__remove:hover{color:#234A67;text-decoration:underline}' +
    '.rb-build__body{display:flex;gap:1.4rem;padding:1.4rem}' +
    '.rb-build__img{width:104px;height:104px;object-fit:contain;flex:0 0 auto;background:#fff}' +
    '.rb-build__lines{flex:1 1 auto;min-width:0}' +
    '.rb-build__row{padding:.5rem 0}' +
    '.rb-build__row+.rb-build__row{border-top:1px solid rgba(35,74,103,.12)}' +
    '.rb-build__name{color:#234A67;line-height:1.35}' +
    '.rb-build__meta{color:#6b7a86;font-size:.9em;margin-top:.25rem}' +
    '.rb-build__change{color:#234A67;text-underline-offset:.3rem}' +
    '.rb-build__total{margin-top:.9rem;padding-top:.7rem;text-align:right;font-weight:600;' +
      'color:#234A67;border-top:1px solid rgba(35,74,103,.18)}' +
    '.rb-cart-note{margin:0 0 1.2rem;padding:1rem 1.2rem;border-left:2px solid #a38245;' +
      'background:rgba(163,130,69,.06);color:#234A67;font-size:.92em}' +
    '@media(max-width:600px){.rb-build__body{flex-direction:column}' +
      '.rb-build__img{width:100%;height:180px}}';

  (function injectCss() {
    if (document.getElementById('rb-cart-pair-css')) return;
    var s = document.createElement('style');
    s.id = 'rb-cart-pair-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  })();

  function props(item) { return item.properties || {}; }
  function buildOf(item) { return props(item)[BUILD] || null; }
  function partOf(item) { return props(item)[PART] || null; }

  // Older builds (added before `_rb_part` shipped) have a `_build_id` but no part
  // marker. Fall back to the visible Component copy rather than treating them as
  // unpaired — an un-guarded orphan is the exact bug this file exists for.
  function roleOf(item) {
    var p = partOf(item);
    if (p) return p;
    var c = (props(item)['Component'] || '').toLowerCase();
    if (c.indexOf('stone') >= 0) return 'stone';
    if (c.indexOf('setting') >= 0 || c.indexOf('mounting') >= 0) return 'setting';
    return null;
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* ---------------------------------------------------------------- repair */
  // Returns a promise resolving to the number of orphan SETTING lines removed.
  function repair(cart) {
    var byBuild = {};
    cart.items.forEach(function (it) {
      var b = buildOf(it);
      if (!b) return;
      (byBuild[b] = byBuild[b] || []).push(it);
    });
    var orphans = [];
    Object.keys(byBuild).forEach(function (b) {
      var group = byBuild[b];
      var hasStone = group.some(function (i) { return roleOf(i) === 'stone'; });
      if (hasStone) return;
      group.forEach(function (i) {
        if (roleOf(i) === 'setting') orphans.push(i);
      });
    });
    if (!orphans.length) return Promise.resolve(0);

    // Remove by line KEY, never by index. Indexes shift as lines are removed, so
    // a loop over indexes deletes the wrong rows from the second one onward.
    return orphans.reduce(function (chain, item) {
      return chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });
      });
    }, Promise.resolve()).then(function () { return orphans.length; });
  }

  // ⚠️ THE MESSAGE HAS TO SURVIVE THE RELOAD. The repair ends in `location.reload()`,
  // so a notice painted before it flashes for a frame and is gone — the customer
  // sees an item vanish from their cart and is told nothing. Park the text and
  // render it on the way back in.
  var NOTE_KEY = 'rb-cart-note';

  function notify(n) {
    if (!n) return;
    var piece = n === 1 ? 'setting' : 'settings';
    try {
      sessionStorage.setItem(NOTE_KEY, 'We removed ' + (n === 1 ? 'a ' : n + ' ') + piece +
        ' from your cart. A setting is made around its gemstone, so we do not sell ' +
        'one on its own — pick a stone for it in the builder and it comes straight back.');
    } catch (e) {}
  }

  function showParkedNote() {
    var msg;
    try { msg = sessionStorage.getItem(NOTE_KEY); } catch (e) { return; }
    if (!msg) return;
    try { sessionStorage.removeItem(NOTE_KEY); } catch (e) {}
    var box = document.createElement('div');
    box.className = 'rb-cart-note';
    box.setAttribute('role', 'status');
    box.textContent = msg;
    var host = document.querySelector('#main-cart-items') ||
               document.querySelector('cart-drawer') || document.body;
    host.insertBefore(box, host.firstChild);
  }

  /* ------------------------------------------------------- group the rows */
  // ONE CARD PER BUILD, the way Blue Nile draws it (owner reference 2026-08-02):
  // a header naming the piece with a single REMOVE, the two halves listed inside
  // with their own SKU, price and a **Change** link, and a combined total. The
  // per-half control is CHANGE, never Remove — "remove the stone" is not a thing
  // anyone means; they mean "pick a different one", and offering Remove there is
  // exactly what let a bare setting reach checkout.
  var PAGES = {
    ring:    { settings: '/pages/ring-builder',    stones: '/pages/ring-gemstones' },
    pendant: { settings: '/pages/pendant-builder', stones: '/pages/pendant-gemstones' },
    earring: { settings: '/pages/earring-builder', stones: '/pages/earring-gemstones' }
  };
  var LABEL = { ring: 'Ring', pendant: 'Pendant', earring: 'Earrings' };

  function money(cents) { return '$' + Math.round(cents / 100).toLocaleString('en-US'); }

  function changeHref(item, which) {
    var p = props(item);
    var kind = p['_rb_kind'] || 'ring';
    var pages = PAGES[kind] || PAGES.ring;
    var q = p['_rb_q'] || '';
    if (which === 'setting') {
      // straight back to the setting's own configure view.
      // ⚠️ `_rb_q` ALREADY CONTAINS `style=` — appending it again emitted
      // `?style=225825&…&style=225825&configure=1`. URLSearchParams takes the
      // last, so it happened to work, which is exactly why it would have sat
      // there. Set it through URLSearchParams instead of string-concatenating.
      var u = new URLSearchParams(q);
      if (p['_rb_style']) u.set('style', p['_rb_style']);
      u.set('configure', '1');
      return pages.settings + '?' + u.toString();
    }
    // the stone grid, still carrying the setting so nothing is re-chosen twice
    var origin = /(^|&)origin=lab(&|$)/.test(q) ? '-lab' : '';
    return pages.stones.replace('-gemstones', '-gemstones' + origin) + '?' + q;
  }

  function row(item, which) {
    var p = props(item);
    var wrap = document.createElement('div');
    wrap.className = 'rb-build__row';
    var name = document.createElement('div');
    name.className = 'rb-build__name';
    name.textContent = item.product_title || item.title;
    var meta = document.createElement('div');
    meta.className = 'rb-build__meta';
    var bits = [];
    if (item.sku) bits.push('SKU ' + item.sku);
    bits.push(money(item.line_price));
    if (item.quantity > 1) bits.push('qty ' + item.quantity);
    meta.appendChild(document.createTextNode(bits.join('   ') + '   '));
    var a = document.createElement('a');
    a.className = 'rb-build__change';
    a.href = changeHref(item, which);
    a.textContent = 'Change';
    meta.appendChild(a);
    wrap.appendChild(name);
    wrap.appendChild(meta);
    return wrap;
  }

  function group(cart) {
    var rows = document.querySelectorAll('[id^="CartItem-"]');
    if (!rows.length) return;
    var byIndex = {};
    cart.items.forEach(function (it, i) { byIndex[i + 1] = it; });

    // collect the DOM row for each line of each build
    var builds = {};
    rows.forEach(function (el) {
      var m = (el.id || '').match(/CartItem-(\d+)/);
      if (!m) return;
      var item = byIndex[parseInt(m[1], 10)];
      var b = item && buildOf(item);
      if (!b) return;
      (builds[b] = builds[b] || []).push({ el: el, item: item, role: roleOf(item) });
    });

    Object.keys(builds).forEach(function (b) {
      var parts = builds[b];
      var setting = parts.filter(function (x) { return x.role === 'setting'; })[0];
      var stone = parts.filter(function (x) { return x.role === 'stone'; })[0];
      if (!setting || !stone) return;                 // repair() handles orphans
      if (setting.el.getAttribute('data-rb-grouped')) return;

      var kind = props(setting.item)['_rb_kind'] || 'ring';
      var card = document.createElement('div');
      card.className = 'rb-build';
      card.setAttribute('data-rb-build', b);

      var head = document.createElement('div');
      head.className = 'rb-build__head';
      var h = document.createElement('span');
      h.className = 'rb-build__title';
      h.textContent = (LABEL[kind] || 'Ring') + ' (Completed)';
      head.appendChild(h);
      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'rb-build__remove';
      rm.textContent = 'Remove';
      // ONE Remove, and it takes BOTH halves. This is the control that used to
      // exist twice and delete one line at a time.
      rm.addEventListener('click', function () {
        rm.disabled = true;
        parts.reduce(function (chain, x) {
          return chain.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: x.item.key, quantity: 0 })
            });
          });
        }, Promise.resolve()).then(function () { location.reload(); });
      });
      head.appendChild(rm);
      card.appendChild(head);

      var body = document.createElement('div');
      body.className = 'rb-build__body';
      var img = (setting.item.image || stone.item.image);
      if (img) {
        var im = document.createElement('img');
        im.className = 'rb-build__img';
        im.src = img;
        im.alt = '';
        im.loading = 'lazy';
        body.appendChild(im);
      }
      var lines = document.createElement('div');
      lines.className = 'rb-build__lines';
      lines.appendChild(row(setting.item, 'setting'));
      lines.appendChild(row(stone.item, 'stone'));

      var total = document.createElement('div');
      total.className = 'rb-build__total';
      total.textContent = money(setting.item.line_price + stone.item.line_price);
      lines.appendChild(total);
      body.appendChild(lines);
      card.appendChild(body);

      // Swap the card in where the setting row was, and take both rows out. The
      // line items themselves are untouched — this is presentation only.
      setting.el.parentNode.insertBefore(card, setting.el);
      setting.el.setAttribute('data-rb-grouped', '1');
      parts.forEach(function (x) { x.el.setAttribute('hidden', 'hidden'); });
    });
  }

  /* ---------------------------------------------------------------- run */
  var running = false;
  function run() {
    if (running) return;
    running = true;
    showParkedNote();
    getCart().then(function (cart) {
      return repair(cart).then(function (n) {
        if (n) {
          // 🚨 RELOAD ONCE, EVER. The repair ends in a reload so the totals and
          // the checkout button agree with the cart — but if a removal silently
          // fails (a 422, a line key that moved), the next load finds the same
          // orphan and reloads again, forever, on the page where the customer
          // keeps their money. sessionStorage is the brake: a second pass that
          // still sees orphans leaves them visible and says nothing rather than
          // spinning. Cleared as soon as a clean cart is seen.
          var KEY = 'rb-cart-repaired';
          if (sessionStorage.getItem(KEY)) {
            if (window.console) {
              console.warn('[rb-cart-pair] orphan setting persisted after a repair '
                           + 'pass — not reloading again');
            }
            group(cart);
            return;
          }
          try { sessionStorage.setItem(KEY, '1'); } catch (e) {}
          notify(n);
          location.reload();
          return;
        }
        try { sessionStorage.removeItem('rb-cart-repaired'); } catch (e) {}
        group(cart);
      });
    }).catch(function () { /* never let a cart guard break the cart */ })
      .then(function () { running = false; });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  // Edge re-renders the cart in place on quantity changes and drawer opens, so
  // re-check after those rather than only on first paint.
  document.addEventListener('cart:refresh', run);
  document.addEventListener('cart:updated', run);
  document.addEventListener('cart-drawer:open', run);
})();
